import {ApolloClient, ApolloError, ApolloQueryResult, ServerError} from "@apollo/client";
import log from "loglevel";
import {
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
  SortModelItem
} from "@ag-grid-community/core";
import {
  Driver,
  DriverFilterInput,
  SearchableDriverSortInput,
  SearchableSortDirection,
  SearchDriversEnhancedDocument
} from "../../generated/graphql";
import {createCondition, makeQueryStringFilter} from "../../utils/FilterUtils";
import {Constants} from "../../constants/constants";
import {getColumnIdToFields} from "../../utils/ag-grid/AGGridUtils";

type Decorator = (params: IServerSideGetRowsParams, results: Driver[], res: ApolloQueryResult<any>) => Promise<void>;

type CreateJobsServerSideDatasourceProps = {
  client: ApolloClient<any>;
  defaultFields?: string[];
  excludeFields?: string[];
  onDatasourceFail?(error: ApolloError | undefined): void;
  decorateResults?: Decorator[];
  tokenExpiry: Date | undefined;
};

export const CustomOperatorFields = new Map<string | undefined, string>([]);

export const createDriverServerSideDatasource = ({
  client,
  excludeFields,
  onDatasourceFail,
  decorateResults,
  tokenExpiry
}: CreateJobsServerSideDatasourceProps): IServerSideDatasource => {
  return {
    getRows: function (params: IServerSideGetRowsParams) {
      const {startRow, filterModel, sortModel}: IServerSideGetRowsRequest = params.request;
      const colIdToFields: {[key: string]: string[]} = getColumnIdToFields(
        params.columnApi.getAllGridColumns(),
        excludeFields
      );

      let filter: DriverFilterInput = {
        driver: {
          and: [{driverStatus: {eq: "A"}}, ...buildDriversFilter(filterModel)]
        }
      };

      if (params.context?.globalFilter) {
        filter = {
          driver: {
            and: [params.context.globalFilter, {...filter.driver}]
          }
        };
      }

      const globalQuery = params.context?.globalQuery;
      if (globalQuery) {
        filter.queryString = makeQueryStringFilter(globalQuery, ["name", "driverCode"]);
      }

      const sort = [...buildDriversSort(sortModel, colIdToFields)];

      let retry = 0;

      const getData = async () => {
        try {
          const res = await client.query({
            query: SearchDriversEnhancedDocument,
            variables: {
              offset: startRow,
              limit: params.api.paginationGetPageSize(),
              filter: filter,
              sort: sort
            }
          });

          const serverSideResults = (res.data?.searchDriversEnhanced.items as Driver[]).map((j) => {
            return {...j} as Driver;
          });

          if (decorateResults) {
            for await (const decorator of decorateResults) {
              await decorator(params, serverSideResults, res);
            }
          }

          params.success({
            rowData: serverSideResults,
            rowCount: res.data.searchDriversEnhanced.total
          });
          retry = 0;
        } catch (error) {
          handleError(error as ApolloError);
        }
      };

      const handleError = (err: ApolloError) => {
        const netError = err.networkError as ServerError;
        if (netError && netError.statusCode === 401) {
          //This will retry at the ApolloLink level
          log.debug("Cancel loading with a fake success");
          params.success({rowData: [], rowCount: 0});
          return;
        }
        //Refetch if type error
        if (err.name === "TypeError") {
          log.debug("Retrying by TypeError:", err);
          getData();
          return;
        }
        if (err.message.startsWith("No more mocked responses")) {
          log.debug("No more mocked responses");
          params.success({rowData: [], rowCount: 0});
          return;
        }
        if (retry === Constants.MAX_NUMBER_OF_RETRY) {
          log.error("Jobs grid error", {
            error: err,
            raw: JSON.stringify(err),
            tokenExpiry: tokenExpiry,
            clientDateTime: new Date()
          });
          onDatasourceFail?.(err);
          params.fail();
          return;
        }

        retry++;
        log.debug(`Retrying ${retry} by error:`, err);
        getData();
      };
      onDatasourceFail?.(undefined);
      getData();
    }
  };
};

const buildDriversSort = (
  sortModel: SortModelItem[],
  colIdToFields: {[key: string]: string[]}
): SearchableDriverSortInput[] => {
  const newSortModel = [...sortModel];
  const newColIdToFields = {...colIdToFields};
  const sortInputs = newSortModel
    .filter((sm) => newColIdToFields[sm.colId] !== undefined)
    .map((sm) => {
      return {
        field: sm.colId,
        direction: sm.sort === "asc" ? SearchableSortDirection.Asc : SearchableSortDirection.Desc
      } as SearchableDriverSortInput;
    });

  if (sortInputs.length === 0) {
    sortInputs.push({
      field: "name",
      direction: SearchableSortDirection.Asc
    } as SearchableDriverSortInput);
  }

  return sortInputs;
};

const buildDriversFilter = (filterModel: any): any[] => {
  const filters: any[] = [];
  if (filterModel) {
    Object.keys(filterModel).forEach(function (columnKey) {
      let modelFilter = filterModel[columnKey];
      if (columnKey === "dispatchGroupId" || columnKey === "vehicleTypeId") {
        modelFilter = {
          ...modelFilter,
          filterType: "setNumber"
        };
      }
      const condition = createCondition(columnKey.replaceAll(".", "_"), modelFilter, columnKey, CustomOperatorFields);
      filters.push(condition);
    });
  }
  return filters;
};
